import { useLocation } from 'react-router-dom';

import { selectBulkErrors, selectCurrentTableKey } from '@/redux/selectors/dataTableSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { applyFilter, clearFilters, setBulkErrors } from '@/redux/slices/dataTableSlice';
import styled from '@emotion/styled';
import Modal from './Modal/Modal';
import PolarisIcon from './PolarisIcon';
import ModalFooterButtons from './Modal/ModalFooterButtons';
import { Grid } from '@mui/material';

const BulkActionErrorModal = () => {
  const dispatch = useAppDispatch();
  const errors = useAppSelector(selectBulkErrors);
  const currentTableKey = useAppSelector(selectCurrentTableKey);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const idFromParams = params.get('id');

  const getPrefix = () => {
    switch (currentTableKey) {
      case 'orders':
        return 'W';
      case 'pendingAssets':
        return 'A';
      case 'jobs':
        return 'J';
      case 'tasks':
        return 'T';
      case 'fileIngest':
        return 'R';
      default:
        return;
    }
  };

  const errorLink = (id: string) => {
    switch (currentTableKey) {
      case 'orders':
        dispatch(clearFilters(undefined));
        dispatch(applyFilter({ type: 'filter_id', selections: ['W' + id, 'order_hid_filter'] }));
        return;
      case 'pendingAssets':
        dispatch(clearFilters(undefined));
        dispatch(applyFilter({ type: 'filter_id', selections: ['A' + id, 'job_asset_hid_filter'] }));
        return;
      case 'jobs':
        return window.open(`/jobs/${id}`, '_blank');
      case 'tasks':
        if (id === idFromParams) {
          clearErrors();

          return;
        }

        return window.open(`/tasks?page=1&id=${id}`, '_blank');
      case 'fileIngest':
        return window.open(`/ingest-management/${id}`, '_blank');
      default:
        break;
    }
  };

  const getFilterName = () => {
    switch (currentTableKey) {
      case 'orders':
        return 'order';
      case 'pendingAssets':
        return 'job_asset';
      case 'jobs':
        return 'job';
      case 'tasks':
        return 'task';
      case 'fileIngest':
        return 'ingest_record';
      default:
        return;
    }
  };

  const viewAll = () => {
    const errorIds = errors.map((error) => (error[0] ? getPrefix() + error[0] : ''));
    dispatch(clearFilters(undefined));
    dispatch(applyFilter({ type: 'filter_id', selections: [errorIds, getFilterName() + '_hid_filter'] }));
    clearErrors();
  };

  const clearErrors = () => {
    dispatch(setBulkErrors([]));
  };

  const columns = [
    {
      index: 0,
      name: 'ID',
      col: 3
    },
    {
      index: 1,
      name: 'Error Message',
      col: 9
    }
  ];

  return (
    <StyledModal
      open={!!errors.length}
      title="Bulk Action Errors"
      onClose={() => clearErrors()}
      icon={<PolarisIcon icon="error" height={32} />}
      maxWidth={600}
    >
      <Subtitle>These items failed to process. Click an ID to view details or View All to filter the table.</Subtitle>
      <div>
        <ErrorTable>
          <HeaderRow container>
            {columns.map((column) => (
              <Grid item xs={column.col} key={`header-${column.index}`}>
                {column.name}
              </Grid>
            ))}
          </HeaderRow>
          <ScrollableRows>
            {errors.map((error, index) => (
              <ErrorRow container key={`row-${index}`}>
                {columns.map((column) => (
                  <Grid item xs={column.col} key={`row-${index}-col-${column.index}`} style={{ padding: '8px 0', wordBreak: 'break-word' }}>
                    {column.index === 0 ? (
                      <ErrorLink key={error[0]} onClick={() => errorLink(error[0])}>
                        {getPrefix()}
                        {error[0]}
                      </ErrorLink>
                    ) : (
                      error[1]
                    )}
                  </Grid>
                ))}
              </ErrorRow>
            ))}
          </ScrollableRows>
        </ErrorTable>
      </div>
      <ModalFooterButtons
        cancel={() => clearErrors()}
        submit={{
          onClick: viewAll,
          text: 'View All'
        }}
        divider={false}
      />
    </StyledModal>
  );
};

export default BulkActionErrorModal;

const ErrorTable = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderRow = styled(Grid)`
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  opacity: 0.5;

  > div {
    padding: ${({ theme }) => theme.spacing(0, 2)};
  }
`;

const ErrorRow = styled(Grid)`
  background: ${({ theme }) => theme.palette.primary.main + '1a'};
  padding: ${({ theme }) => theme.spacing(1, 2)};
  margin: ${({ theme }) => theme.spacing(1, 0)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;

const ErrorLink = styled.a`
  text-decoration: none;
  color: ${({ theme }) => theme.palette.primary.main};
  cursor: pointer;
`;

const ScrollableRows = styled.div`
  max-height: 300px;
  overflow-y: scroll;
`;

const StyledModal = styled(Modal)`
  .MuiPaper-root > .MuiGrid-root > div:first-of-type > span {
    background: ${(props) => props.theme.palette.error.main + '3a'};
    color: ${(props) => props.theme.palette.error.main};
  }

  .MuiPaper-root {
    padding: ${({ theme }) => theme.spacing(2, 4, 1)};
  }
`;

const Subtitle = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  color: ${({ theme }) => theme.palette.text.secondary};
`;
